import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  List,
  ListItem,
  ListItemText,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import api from '../services/api';
import { useFamily } from '../contexts/FamilyContext';
import { useAuth } from '../contexts/AuthContext';

const Families = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { families, currentFamily, selectFamily, createFamily, refreshFamilies } = useFamily();
  const [open, setOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [inviteToken, setInviteToken] = useState('');

  const handleRefresh = useCallback(() => {
    refreshFamilies();
  }, [refreshFamilies]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  const handleCreate = async () => {
    try {
      await createFamily(formData);
      setOpen(false);
      setFormData({ name: '', description: '' });
      navigate('/');
    } catch (error) {
      alert('Failed to create family');
    }
  };

  const copyInviteLink = (family) => {
    const url = `${window.location.origin}/families/join/${family.invite_token}`;
    navigator.clipboard.writeText(url);
    alert('Invite link copied to clipboard!');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4">My Families</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          {families.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={currentFamily?.id || ''}
                onChange={(e) => {
                  const family = families.find(f => f.id === e.target.value);
                  if (family) selectFamily(family);
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <em>Select Active Family</em>;
                  }
                  const family = families.find(f => f.id === selected);
                  return family ? `Active: ${family.name}` : <em>Select Active Family</em>;
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select a Family</em>
                </MenuItem>
                {families.map((family) => (
                  <MenuItem key={family.id} value={family.id}>
                    {family.name}
                    {currentFamily?.id === family.id && ' ✓'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="outlined"
            onClick={() => setJoinOpen(true)}
          >
            Join Family
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            Create Family
          </Button>
        </Box>
      </Box>

      {families.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <GroupIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No families yet
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Create a family to get started, or join an existing one with an invite link
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {families.map((family) => {
            const isOwner = family.owner_id === user?.uid;
            const userMember = family.members?.find(m => m.user_id === user?.uid);
            const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);
            const isCurrent = currentFamily?.id === family.id;

            return (
              <Grid item xs={12} sm={6} md={6} key={family.id}>
                <Card
                  sx={{
                    border: isCurrent ? 2 : 0,
                    borderColor: 'primary.main',
                  }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box>
                        <Typography variant="h5" gutterBottom>
                          {family.name}
                          {isCurrent && (
                            <Chip label="Current" size="small" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        {family.description && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {family.description}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {family.member_count} members
                        </Typography>
                        {isOwner && (
                          <Chip label="Owner" size="small" sx={{ mt: 1 }} />
                        )}
                        {userMember && !isOwner && (
                          <Chip
                            label={userMember.role}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                      {isAdmin && family.invite_token && (
                        <Tooltip title="Copy invite link to share with family members">
                          <IconButton
                            onClick={() => copyInviteLink(family)}
                            size="small"
                            color="primary"
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {family.members && family.members.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Members:
                        </Typography>
                        <List dense>
                          {family.members.slice(0, 5).map((member) => (
                            <ListItem key={member.id} sx={{ py: 0 }}>
                              <ListItemText
                                primary={member.user_name}
                                secondary={member.role}
                              />
                            </ListItem>
                          ))}
                          {family.members.length > 5 && (
                            <Typography variant="body2" color="text.secondary">
                              +{family.members.length - 5} more
                            </Typography>
                          )}
                        </List>
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/families/${family.id}/feed`)}
                    >
                      View Feed
                    </Button>
                    {isOwner && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AdminPanelSettingsIcon />}
                        onClick={() => navigate(`/families/${family.id}/dashboard`)}
                      >
                        Dashboard
                      </Button>
                    )}
                    {!isCurrent && (
                      <Button
                        size="small"
                        onClick={() => selectFamily(family)}
                      >
                        Switch to this Family
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Family</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Family Name"
            margin="normal"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <TextField
            fullWidth
            label="Description (optional)"
            margin="normal"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!formData.name}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={joinOpen} onClose={() => setJoinOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Join Family</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Invite Token or Link"
            margin="normal"
            value={inviteToken}
            onChange={(e) => {
              let token = e.target.value;
              // Extract token from URL if full URL provided
              if (token.includes('/join/')) {
                token = token.split('/join/')[1].split('/')[0];
              }
              setInviteToken(token);
            }}
            placeholder="Enter invite token or paste invite link"
            helperText="Get the invite link from a family member"
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setJoinOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                await api.post(`/families/join/${inviteToken}/`);
                await refreshFamilies();
                setJoinOpen(false);
                setInviteToken('');
                alert('Successfully joined family!');
              } catch (error) {
                alert(error.response?.data?.error || 'Failed to join family');
              }
            }}
            variant="contained"
            disabled={!inviteToken}
          >
            Join
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Families;
